/**
 * Created by Ning Jiang on 5/3/2019.
 */

import {MultiSprites} from "@nolimitcity/slot-game/bin/core/displayobject/MultiSprites";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {
    ISymbolSizeConfig,
    ISymbolStateConfig,
    StateReelSymbol,
    SymbolStateTransition
} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Elastic, Timeline, TimelineLite, TweenLite} from "gsap";
import {PixiesVsPiratesReelSymbol} from "./PixiesVsPiratesReelSymbol";

export class PPWildSymbol extends PixiesVsPiratesReelSymbol {

    private static _multiplierTopOffset:number = 40;
    private static _multiplierBottomOffset:number = -40;

    private _multiplierTop:MultiSprites | null;
    private _multiplierBottom:MultiSprites | null;
    private _currentMultiplier:number = 1;

    private _tree:MultiSprites | null;
    private _treeAnchorY:number;
    private _writeOn:MultiSprites | null;
    private _writeOnAnchorY:number;
    private _nudgeSparkles:TimelineSprite | null;
    private _nudgeSparklesAnchorY:number;

    public get bottomPadding():number {
        if(this._bottomPadding == null) {
            return GameConfig.instance.SYMBOL_BOTTOM_PADDING;
        }

        return this._bottomPadding;
    }

    public set bottomPadding(value:number) {
        if(value === this._bottomPadding) {
            return;
        }
        this._bottomPadding = value;
        for(let stateName in this._stateAnimations) {
            const state = this._stateAnimations[stateName];
            if(state != null) {
                StateReelSymbol.setStackedSymbolStateAnchor(this, state);
            }
        }

        this.updateHeightDisplay();
    }

    constructor(symName:string, reelId:number, state?:ISymbolStateConfig, size?:ISymbolSizeConfig) {
        super(symName, reelId, state, size);
    }

    protected initAnimations():void {
        if(!this._tree) {
            this._tree = new MultiSprites({
                normal : {textures : GameResources.getTextures("wildTree")},
                spin : {textures : GameResources.getTextures("wildTree_spin")}
            });
        }

        if(!this._multiplierTop) {
            this._multiplierTop = new MultiSprites({
                normal : {
                    textures : GameResources.getTextures("wildMultiplier"),
                    anchor : [0.5, 0.5]
                },
                spin : {
                    textures : GameResources.getTextures("wildMultiplier_spin"),
                    anchor : [0.5, 0.5]
                }
            });
        }

        if(!this._multiplierBottom) {
            this._multiplierBottom = new MultiSprites({
                normal : {
                    textures : GameResources.getTextures("wildMultiplier"),
                    anchor : [0.5, 0.5]
                },
                spin : {
                    textures : GameResources.getTextures("wildMultiplier_spin"),
                    anchor : [0.5, 0.5]
                }
            });
        }
    }

    protected initState(stateConfig:ISymbolStateConfig):boolean {
        if(super.initState(stateConfig)) {
            if(this._tree) {
                this.addChild(this._tree);
            }
            if(this._writeOn) {
                this.addChild(this._writeOn);
            }
            if(this._multiplierTop) {
                this.addChild(this._multiplierTop);
            }
            if(this._multiplierBottom) {
                this.addChild(this._multiplierBottom);
            }
            if(this._nudgeSparkles) {
                this.addChild(this._nudgeSparkles);
            }
            return true;
        }
        return false;
    }

    protected addNewStateAnimation(timeline:TimelineLite, newState:SymbolStateTransition, stateAnimation:TimelineSprite, delay:number):void {
        super.addNewStateAnimation(timeline, newState, stateAnimation, delay);

        if(newState.state.keyword === GameConfig.instance.SYMBOL_STATES.spin.keyword) {
            if(this._tree) {
                timeline.add(this._tree.playSprite("spin",
                    {
                        duration : newState.fadeInDuration,
                        vars : [{toVars : {alpha : 1}}]
                    },
                    {
                        duration : newState.fadeOutDuration,
                        vars : [{toVars : {alpha : 0}}]
                    }), delay);
            }
            if(this._writeOn) {
                timeline.add(this._writeOn.playSprite("spin",
                    {
                        duration : newState.fadeInDuration,
                        vars : [{toVars : {alpha : 1}}]
                    },
                    {
                        duration : newState.fadeOutDuration,
                        vars : [{toVars : {alpha : 0}}]
                    }), delay);
            }
            if(this._multiplierBottom && this._multiplierTop) {
                timeline.add(() => {this.updateMultiplier(this._currentMultiplier, false, "spin");}, delay);
                timeline.add(this._multiplierTop.playSprite("spin",
                    {
                        duration : newState.fadeInDuration,
                        vars : [{toVars : {alpha : 1}}]
                    },
                    {
                        duration : newState.fadeOutDuration,
                        vars : [{toVars : {alpha : 0}}]
                    }), delay);
                timeline.add(this._multiplierBottom.playSprite("spin",
                    {
                        duration : newState.fadeInDuration,
                        vars : [{toVars : {alpha : 1}}]
                    },
                    {
                        duration : newState.fadeOutDuration,
                        vars : [{toVars : {alpha : 0}}]
                    }), delay);
            }
        } else {
            if(this._tree) {
                timeline.add(this._tree.playSprite("normal",
                    {
                        duration : newState.fadeInDuration,
                        vars : [{toVars : {alpha : 1}}]
                    },
                    {
                        duration : newState.fadeOutDuration,
                        vars : [{toVars : {alpha : 0}}]
                    }), delay);
            }
            if(this._writeOn) {
                timeline.add(this._writeOn.playSpriteFrames("normal",
                    {
                        duration : newState.fadeInDuration,
                        vars : [{toVars : {alpha : 1}}]
                    },
                    {
                        duration : newState.fadeOutDuration,
                        vars : [{toVars : {alpha : 0}}]
                    }, [-1, -1]), delay);
            }
            if(this._multiplierBottom && this._multiplierTop) {
                timeline.add(() => {this.updateMultiplier(this._currentMultiplier, false, "normal");}, delay);
                timeline.add(this._multiplierTop.playSprite("normal",
                    {
                        duration : newState.fadeInDuration,
                        vars : [{toVars : {alpha : 1}}]
                    },
                    {
                        duration : newState.fadeOutDuration,
                        vars : [{toVars : {alpha : 0}}]
                    }), delay);
                timeline.add(this._multiplierBottom.playSprite("normal",
                    {
                        duration : newState.fadeInDuration,
                        vars : [{toVars : {alpha : 1}}]
                    },
                    {
                        duration : newState.fadeOutDuration,
                        vars : [{toVars : {alpha : 0}}]
                    }), delay);
            }
        }

        if(newState.state.keyword === GameConfig.instance.SYMBOL_STATES.noWin!.keyword) {
            if(this._multiplierBottom && this._multiplierTop) {
                for(let spriteName in this._multiplierTop.sprites) {
                    const toSprite:TimelineSprite = this._multiplierTop.sprites[spriteName];
                    timeline.add(TweenLite.to(toSprite, newState.fadeInDuration, {pixi : {tint : "#555555"}}), delay);
                    const bottomSprite:TimelineSprite = this._multiplierBottom.sprites[spriteName];
                    timeline.add(TweenLite.to(bottomSprite, newState.fadeInDuration, {pixi : {tint : "#555555"}}), delay);
                }
            }
            if(this._tree) {
                for(let spriteName in this._tree.sprites) {
                    const treeSprite:TimelineSprite = this._tree.sprites[spriteName];
                    timeline.add(TweenLite.to(treeSprite, newState.fadeInDuration, {pixi : {tint : "#555555"}}), delay);
                }
            }
            if(this._writeOn) {
                for(let spriteName in this._writeOn.sprites) {
                    const writeOnSprite:TimelineSprite = this._writeOn.sprites[spriteName];
                    timeline.add(TweenLite.to(writeOnSprite, newState.fadeInDuration, {pixi : {tint : "#555555"}}), delay);
                }
            }
        } else {
            if(this._multiplierBottom && this._multiplierTop) {
                for(let spriteName in this._multiplierTop.sprites) {
                    const toSprite:TimelineSprite = this._multiplierTop.sprites[spriteName];
                    timeline.add(TweenLite.to(toSprite, newState.fadeInDuration, {pixi : {tint : "#ffffff"}}), delay);
                    const bottomSprite:TimelineSprite = this._multiplierBottom.sprites[spriteName];
                    timeline.add(TweenLite.to(bottomSprite, newState.fadeInDuration, {pixi : {tint : "#ffffff"}}), delay);
                }
            }
            if(this._tree) {
                for(let spriteName in this._tree.sprites) {
                    const treeSprite:TimelineSprite = this._tree.sprites[spriteName];
                    timeline.add(TweenLite.to(treeSprite, newState.fadeInDuration, {pixi : {tint : "#ffffff"}}), delay);
                }
            }
            if(this._writeOn) {
                for(let spriteName in this._writeOn.sprites) {
                    const writeOnSprite:TimelineSprite = this._writeOn.sprites[spriteName];
                    timeline.add(TweenLite.to(writeOnSprite, newState.fadeInDuration, {pixi : {tint : "#ffffff"}}), delay);
                }
            }
        }
    }

    protected reset(remove:boolean):void {
        if(this._tree) {
            this.removeChild(this._tree);
            this._tree = null;
        }
        if(this._writeOn) {
            this.removeChild(this._writeOn);
            this._writeOn = null;
        }
        if(this._nudgeSparkles) {
            this.removeChild(this._nudgeSparkles);
            this._nudgeSparkles = null;
        }

        if(this._multiplierTop && this._multiplierTop.parent) {
            this.removeChild(this._multiplierTop);
        }
        this._multiplierTop = null;
        if(this._multiplierBottom && this._multiplierBottom.parent) {
            this.removeChild(this._multiplierBottom);
        }
        this._multiplierBottom = null;
        super.reset(remove);
    }

    protected updateHeightDisplay():void {
        const anchorYOffset:number = -this.stackedSymName!.totalNum * this.symHeight * 0.5
            - (this.stackedSymName!.totalNum - 1) * this.bottomPadding * 0.5
            + (this.stackedSymName!.index + 0.5) * this.symHeight
            + this.stackedSymName!.index * this.bottomPadding;

        const treeHeight:number = 414;
        this._treeAnchorY = MathHelper.roundToDecimals((treeHeight * 0.5 + anchorYOffset) / treeHeight, 4);
        for(let name in this._tree!.sprites) {
            this._tree!.sprites[name].anchor.set(0.5, this._treeAnchorY);
        }

        const writeOnHeight:number = 506;
        this._writeOnAnchorY = MathHelper.roundToDecimals((writeOnHeight * 0.5 + anchorYOffset) / writeOnHeight, 4);
        this.updateWriteOnAnchor();

        const sparklesHeight:number = 460;
        this._nudgeSparklesAnchorY = MathHelper.roundToDecimals((sparklesHeight * 0.5 + anchorYOffset) / sparklesHeight, 4);
        if(this._nudgeSparkles) {
            this._nudgeSparkles.anchor.set(0.5, this._nudgeSparklesAnchorY);
        }

        this._multiplierTop!.position.set(0, (-this.stackedSymName!.index - 0.5) * this.symHeight - this.stackedSymName!.index * this.bottomPadding + PPWildSymbol._multiplierTopOffset);
        this._multiplierBottom!.position.set(0, (this.stackedSymName!.totalNum - this.stackedSymName!.index - 0.5) * this.symHeight + (this.stackedSymName!.totalNum - this.stackedSymName!.index - 1) * this.bottomPadding + PPWildSymbol._multiplierBottomOffset);
    }

    public playWriteOn(animate:boolean):Timeline {
        this.createWriteOn();
        return this._writeOn!.playSpriteFrames("normal", {
            duration : 0,
            vars : [{toVars : {alpha : 1}}]
        }, undefined, animate ? [0] : [-1]);
    }

    private createWriteOn():void {
        if(!this._writeOn) {
            this._writeOn = new MultiSprites({
                normal : {textures : GameResources.getTextures("wildWriteOn")},
                spin : {textures : GameResources.getTextures("wildWriteOn_spin")}
            });
            this.updateWriteOnAnchor();
            this.addChild(this._writeOn);
        }
        if(this._multiplierTop) {
            this.addChild(this._multiplierTop);
        }
        if(this._multiplierBottom) {
            this.addChild(this._multiplierBottom);
        }
    }

    private updateWriteOnAnchor():void {
        if(this._writeOn) {
            for(let name in this._writeOn!.sprites) {
                this._writeOn!.sprites[name].anchor.set(0.5, this._writeOnAnchorY);
            }
        }
    }

    public playNudgeSparkles():void {
        if(!this._nudgeSparkles) {
            this._nudgeSparkles = new TimelineSprite(GameResources.getTextures("wildNudgeSparkles"));
            this._nudgeSparkles.anchor.set(0.5, this._nudgeSparklesAnchorY);
            this.addChild(this._nudgeSparkles);
        }
        this._nudgeSparkles.getAnimation();
    }

    public updateMultiplier(multiplier:number, isAnimated:boolean, spriteName?:string):TimelineLite | null {
        // Logger.logDev(`Updating multiplier to ${multiplier}`);
        const timeline:TimelineLite = new TimelineLite();

        //dig for usage and check with ning
        if(this._multiplierTop !== null || this._multiplierBottom !== null) {
            if(this.stack) {
                (<PPWildSymbol[]>this.stack!.symbols).forEach((wild:PPWildSymbol) => {
                    if(wild) {
                        timeline.add(wild.updateMultiplierInternal(multiplier, wild === this ? isAnimated : false, spriteName), 0);
                    }
                });
            } else {
                timeline.add(this.updateMultiplierInternal(multiplier, isAnimated, spriteName), 0);
            }
        }
        return timeline;
    }

    private updateMultiplierInternal(multiplier:number, isAnimated:boolean, spriteName?:string):TimelineLite | null {
        // Logger.logDev(`UpdateMultiplier ${this.symName}, visible = ${this.visible}, multiplier = ${multiplier}`);
        this._currentMultiplier = multiplier;
        if(this._multiplierTop == null || this._multiplierBottom == null) {
            return null;
        }
        const index:number = this._currentMultiplier - 1;
        let topSprite:TimelineSprite | undefined = undefined;
        let bottomSprite:TimelineSprite | undefined = undefined;
        if(spriteName == null) {
            if(this._multiplierTop.currentSprite) {
                // in restore state in freespin, the multiplier is set before currentSprite.
                topSprite = this._multiplierTop.currentSprite;
            }
            if(this._multiplierBottom.currentSprite) {
                bottomSprite = this._multiplierBottom.currentSprite;
            }
        } else {
            topSprite = this._multiplierTop.sprites[spriteName];
            bottomSprite = this._multiplierBottom.sprites[spriteName];
        }

        const timeline:TimelineLite = new TimelineLite();
        if(topSprite) {
            timeline.add(topSprite.getAnimation([index, index]), 0);
            if(isAnimated) {
                timeline.add(TweenLite.fromTo(topSprite.scale, 0.4,
                    {x : 1.5, y : 1.5, ease : Elastic.easeIn.config(1, 1)},
                    {x : 1, y : 1}), 0);
            }
        }
        if(bottomSprite) {
            timeline.add(bottomSprite.getAnimation([index, index]), 0);
            if(isAnimated) {
                timeline.add(TweenLite.fromTo(bottomSprite.scale, 0.4,
                    {x : 1.5, y : 1.5, ease : Elastic.easeIn.config(1, 1)},
                    {x : 1, y : 1}), 0);
            }
        }

        return timeline;
    }
}